import { LoaderCircle } from "lucide-react";
import { WORKBENCH_DRAFT_FLUSH_EVENT } from "@fluentry/shared-workbench/workbench-core";
import React from "react";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { AppContext, type AuthSessionData } from "../context/app-context";
import { authClient, resolveProductAppApiBaseUrl } from "../lib/auth-client";
import { useAppShellRouteState } from "../hooks/use-app-shell-route-state";
import { useAppSessionBridge } from "../hooks/use-app-session-bridge";
import { revokeMobileExchangeSession } from "../lib/mobile-exchange";
import { captureProductAppException, setProductAppUserContext } from "../lib/sentry";
import { shellBackgroundClass } from "../shell-constants";
import { AppShellFrame } from "./app-shell-frame";
import { RouteFallback } from "./route-fallback";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error) {
    void captureProductAppException(error, { feature: "app-shell" });
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Something went wrong. Please reload the page.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

function flushWorkbenchDraftBeforeSessionChange() {
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const complete = (saved: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(saved);
    };
    // Session teardown must not hang forever if a mounted workbench listener fails to complete.
    const timeoutId = window.setTimeout(() => complete(false), 5000);
    const event = new CustomEvent(WORKBENCH_DRAFT_FLUSH_EVENT, {
      cancelable: true,
      detail: {
        complete,
      },
    });

    const handled = !window.dispatchEvent(event);
    if (!handled) {
      complete(true);
    }
  });
}

export function AppShell() {
  const authState = authClient.useSession();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const matches = useRouterState({ select: (state) => state.matches });
  const routeState = useAppShellRouteState(pathname, matches);
  const sessionBridge = useAppSessionBridge(authState.data ?? null);
  const apiBaseUrl = React.useMemo(() => resolveProductAppApiBaseUrl(), []);
  const [sessionActionError, setSessionActionError] = React.useState<string | null>(null);
  const effectiveAuthSession = React.useMemo(
    () => (authState.data ? (authState.data as AuthSessionData) : null),
    [authState.data],
  );

  const contextValue = {
    session: sessionBridge.session,
    authSession: effectiveAuthSession,
    mobileSession: sessionBridge.mobileSession,
    isNativeSurface: sessionBridge.isNativeSurface,
    mobileBridgeConfig: sessionBridge.mobileBridgeConfig,
    mobileBridgeError: sessionBridge.mobileBridgeError,
    apiBaseUrl,
    refetchSession: authState.refetch,
    setMobileSession: sessionBridge.setMobileSession,
    clearMobileBridgeError: sessionBridge.clearMobileBridgeError,
    openNativeBrowserSignIn: sessionBridge.openNativeBrowserSignIn,
  };

  React.useEffect(() => {
    // Refresh the shared Sentry scope here so route-level failures inherit the active user and workspace without repeating plumbing.
    setProductAppUserContext({
      user: effectiveAuthSession?.user
        ? {
            id: effectiveAuthSession.user.id,
            email: effectiveAuthSession.user.email,
            role: sessionBridge.session.user?.role ?? null,
          }
        : null,
      activeOrganizationId:
        typeof effectiveAuthSession?.session.activeOrganizationId === "string"
          ? effectiveAuthSession.session.activeOrganizationId
          : null,
      isNativeSurface: sessionBridge.isNativeSurface,
    });
  }, [effectiveAuthSession, sessionBridge.isNativeSurface, sessionBridge.session.user?.role]);

  if (authState.isPending || (sessionBridge.isNativeSurface && !sessionBridge.mobileReady)) {
    return (
      <AppErrorBoundary>
        <main
          className={`flex min-h-screen items-center justify-center ${shellBackgroundClass} text-foreground`}
        >
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border bg-card shadow-sm">
              <LoaderCircle className="h-5 w-5 animate-spin text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">Loading…</p>
          </div>
        </main>
      </AppErrorBoundary>
    );
  }

  async function handleSignOut() {
    // Centralize logout orchestration here so native session cleanup and Better Auth sign-out cannot drift apart.
    setSessionActionError(null);
    try {
      const draftFlushed = await flushWorkbenchDraftBeforeSessionChange();
      if (!draftFlushed) {
        setSessionActionError(
          "Sign out paused because the current draft could not be saved. Save the draft and retry.",
        );
        return;
      }

      if (sessionBridge.mobileSession) {
        await revokeMobileExchangeSession(sessionBridge.mobileSession);
        await sessionBridge.setMobileSession(null);
        window.location.href = "/login";
        return;
      }

      await authClient.signOut();
      window.location.href = "/login";
    } catch (error) {
      void captureProductAppException(error, {
        feature: "app-shell",
        action: "sign-out",
      });
      setSessionActionError(
        "Sign out could not complete. Please retry so this session is revoked.",
      );
    }
  }

  if (routeState.isStandaloneRoute) {
    return (
      <AppErrorBoundary>
        <AppContext.Provider value={contextValue}>
          <main
            className={`mobile-safe-shell min-h-screen ${routeState.shellSurfaceClass} text-foreground`}
          >
            <React.Suspense fallback={<RouteFallback />}>
              <Outlet />
            </React.Suspense>
          </main>
        </AppContext.Provider>
      </AppErrorBoundary>
    );
  }

  return (
    <AppErrorBoundary>
      <AppContext.Provider value={contextValue}>
        {/* Keep the frame purely presentational so AppShell remains the only place that wires route state to session state. */}
        <AppShellFrame
          authSession={effectiveAuthSession}
          isSettingsRoute={routeState.isSettingsRoute}
          mobileNavOpen={routeState.mobileNavOpen}
          onCloseMobileNav={() => routeState.setMobileNavOpen(false)}
          onCreateDraft={routeState.handleCreateDraft}
          onNavigateToSection={routeState.handleNavigateToWorkbenchSection}
          onOpenMobileNav={() => routeState.setMobileNavOpen(true)}
          onRefetchSession={authState.refetch}
          onSignOut={handleSignOut}
          pageMeta={routeState.pageMeta}
          sectionLabel={routeState.sectionLabel}
          session={sessionBridge.session}
          sessionActionError={sessionActionError}
          shellSurfaceClass={routeState.shellSurfaceClass}
        />
      </AppContext.Provider>
    </AppErrorBoundary>
  );
}
